import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ThumbSettings } from '@syncfusion/ej2-angular-charts';
import { CloseEventArgs, DialogComponent } from '@syncfusion/ej2-angular-popups';
import { catchError, finalize, Observable, Subject, Subscription, tap } from 'rxjs';
import { PessoaDoencaConsultaDto, PessoaDoencaInativacaoDto } from 'src/app/dtos/doencas/pessoa-doenca-dto';
import { SelectDataSource } from 'src/app/libs/ax-select/select-data-source';
import { validIntId } from 'src/app/libs/ax-validators/ax-validators';
import { ToastService } from 'src/app/libs/toast/toast.service';
import { Cid10Service } from 'src/app/servicos/cid10.service';
import { PessoaDoencaService } from 'src/app/servicos/pessoa-doenca.service';
import { AtendimentoAtualConfiguracao } from '../../atendimento-medico/atendimento-cadastro/atendimento-cadastro.component';
import { ConfirmService } from 'src/app/libs/confirm/confirm.service';
import { TranslateService } from '@ngx-translate/core';
import { ALERTA, DOENCA, ERRO } from 'src/app/i18n/constantes';

interface InativarDoencaForm {
  id: FormControl<number>,
  cid10Codigo: FormControl<string>,
  dataCura: FormControl<Date | null>,
  dataAtendimento: FormControl<Date | null>,
  atendimentoMedicoId: FormControl<number | null>,
  descricao: FormControl<string | null>,
}

@Component({
  selector: 'app-doenca-inativacao',
  templateUrl: './doenca-inativacao.component.html',
  styleUrls: ['./doenca-inativacao.component.less']
})
export class DoencaInativacaoComponent implements OnInit {
  modalOk: boolean = false;
  
  @Input()
  atendimentoAtualConfiguracao: AtendimentoAtualConfiguracao | null = null;
  
  @ViewChild('modal')
  modal!: DialogComponent;

  submetido: boolean = false;
  emOperacao: boolean = false;
  temAlteracao: boolean = false;

  subsModelo: Subscription | null = null;
  
  ultimaDescricao: string | null = null;

  modelo: FormGroup<InativarDoencaForm> = this.fb.group<InativarDoencaForm>({
    id: this.fb.nonNullable.control<number>(0, validIntId()),
    cid10Codigo: this.fb.nonNullable.control<string>('', Validators.required),
    dataCura: this.fb.control<Date | null>(null),
    dataAtendimento: this.fb.control<Date | null>(null),
    atendimentoMedicoId: this.fb.control<number | null>(null),
    descricao: this.fb.control<string | null>(null),
  });

  dsCids10: SelectDataSource = this.cid10Servico.dsCids10();

  assunto: Subject<boolean> = new Subject<boolean>();

  constructor(private fb: FormBuilder, 
    private cid10Servico: Cid10Service, 
    private toast: ToastService,
    private confirm: ConfirmService,
    private translate: TranslateService,
    private pessoaDoencaServico: PessoaDoencaService) { }

  ngOnInit(): void {
    this.subsModelo = this.modelo.valueChanges.subscribe(() =>  this.temAlteracao = true);
  }

  ngOnDestroy(): void {
    this.subsModelo?.unsubscribe();
  }

  inativar() {
    this.submetido = true;

    if(this.emOperacao)
      return;

    if(!this.modelo.valid)
      return;

    let doenca: PessoaDoencaInativacaoDto = {
      id: this.modelo.controls.id.value,
      dataCura: null,
      atendimentoMedicoId: this.modelo.controls.atendimentoMedicoId.value,
      dataAtendimento: this.modelo.controls.dataAtendimento.value,
      descricao:  this.modelo.controls.descricao.value
    };

    this.emOperacao = true;
    this.pessoaDoencaServico.inativarDoenca(doenca)
    .pipe(
      catchError(err => {
        this.toast.showWarning(this.translate.instant(DOENCA.ERRO.INATIVACAO));
        throw err;
      }),
      finalize(() => {
        this.emOperacao = false;
        this.modal.hide();
      })
    )
    .subscribe(() => {
      this.temAlteracao = false;
      this.emOperacao = false;
      this.toast.showSuccess(this.translate.instant(DOENCA.SUCESSO.INATIVACAO));
      this.modal.hide();
    });
    
  }

  fechandoModal(args: CloseEventArgs) {
    if(this.emOperacao) {
      args.cancel = true;
      return;
    }

    if(this.temAlteracao) {
      args.cancel = true;
      this.confirmarFechamento();
      return;
    }

    this.assunto.next(true);
  }

  abrir(id: number): Observable<boolean> {
    this.submetido = false;
    
    this.modal.show(true);

    this.carregar(id);

    return new Observable((subscriber) => {
      let subs = this.assunto.subscribe((val) => {
        subs.unsubscribe();
        subscriber.next(val);
        subscriber.complete();
      });

      return { unsubscribe: () => subs.unsubscribe() }
    });
  }
  
  carregar(id: number) {
    this.pessoaDoencaServico.consultarDoenca(id)
    .pipe(
      catchError((err) => {
        this.toast.showError(this.translate.instant(ERRO.CARREGAMENTO));
        this.modal.hide();
        throw err;
      }),
      tap((dto: PessoaDoencaConsultaDto) => {
        this.ultimaDescricao = dto.descricao;
        this.modelo.controls.cid10Codigo.setValue(dto.cid10Id, { emitEvent: false });
        this.modelo.controls.descricao.setValue('', { emitEvent: false });
        this.modelo.controls.dataAtendimento.setValue(this.atendimentoAtualConfiguracao?.data ?? null, { emitEvent: false });
        this.modelo.controls.atendimentoMedicoId.setValue(this.atendimentoAtualConfiguracao?.id ?? null, { emitEvent: false });
        this.modelo.controls.dataCura.setValue(dto.dataCura, { emitEvent: false });
        this.modelo.controls.id.setValue(dto.id, { emitEvent: false });
      })
    )
    .subscribe()
  }

  confirmarFechamento() {
    this.confirm
    .confirm(this.translate.instant(ALERTA.OPERACAO_PENDENTE), { height: '220px' })
    .subscribe(val => {
      if(val) {
        this.cancelar();
      }
    })
  }

  cancelar() {
    this.temAlteracao = false;
    this.modal.hide();
  }
}
